import type { MessageKey } from "~/i18n/messages/pt-BR";
import { TaxApiError } from "./errors";
import { isPureCompute } from "./replay-safety";

/**
 * What a screen tells the person when a call fails. One vocabulary for every
 * calculator surface, so no failure ends in a blank panel or a bare "error":
 * each kind says what happened, what to do next, and whether a retry is safe.
 */
export type FailureKind =
  | "offline"
  | "network"
  | "timeout"
  | "gatewayTimeout"
  | "unavailable"
  | "notAvailable"
  | "rateLimited"
  | "inProgress"
  | "validation"
  | "unauthenticated"
  | "forbidden"
  | "notFound"
  | "conflict"
  | "tooLarge"
  | "server"
  | "unexpected";

export type FailureAdvice = {
  kind: FailureKind;
  whatKey: MessageKey;
  todoKey: MessageKey;
  /** Trying the same request again is a sensible next step. */
  retryable: boolean;
  /**
   * A write whose answer never arrived (gateway deadline, dropped connection):
   * the server may have finished it. The screen re-checks before telling the
   * person to resend, and warns them not to duplicate it.
   */
  maybeCommitted: boolean;
  correlationId: string | undefined;
  requestId: string | undefined;
};

const RETRYABLE_KINDS: ReadonlySet<FailureKind> = new Set([
  "offline",
  "network",
  "timeout",
  "gatewayTimeout",
  "unavailable",
  "rateLimited",
  "inProgress",
  "server",
  "unexpected",
]);

/** Codes that mean "the answer was lost", not "the server said no". */
const ANSWER_LOST_CODES: ReadonlySet<string> = new Set(["TIMEOUT", "GATEWAY_TIMEOUT"]);

/** A non-GET request went out and its answer never arrived (deadline, dropped connection). */
export function answerLost(error: unknown): boolean {
  if (!(error instanceof TaxApiError)) return false;
  if (!error.method || error.method === "GET") return false;
  if (error.offline) return false;
  return ANSWER_LOST_CODES.has(error.code) || error.status === 502 || error.status === 504;
}

/**
 * True when a write was sent but its outcome is unknown. Reads never qualify,
 * and neither does a POST that only computes (inspect, coverage, estimate):
 * nothing was stored, so repeating it is plainly safe.
 */
export function mayHaveCommitted(error: unknown): boolean {
  if (!answerLost(error)) return false;
  return !(error instanceof TaxApiError && error.path && isPureCompute(error.path));
}

export function failureKindOf(error: unknown): FailureKind {
  if (!(error instanceof TaxApiError)) return "unexpected";
  switch (error.code) {
    case "NETWORK":
      return error.offline ? "offline" : "network";
    case "TIMEOUT":
      return "timeout";
    case "GATEWAY_TIMEOUT":
      return "gatewayTimeout";
    case "UNEXPECTED_RESPONSE":
      return "unexpected";
    case "UNAUTHENTICATED":
      return "unauthenticated";
    case "PERMISSION_DENIED":
    case "SEPARATION_OF_DUTIES_VIOLATION":
      return "forbidden";
    case "NOT_FOUND":
      return "notFound";
    case "OPERATION_PRICING_UNRESOLVED":
    case "OPERATION_NOT_AVAILABLE":
    case "NO_PUBLISHED_RULESET":
    case "RUN_ACTIVE":
      return "notAvailable";
    case "BATCH_TOO_LARGE":
      return "tooLarge";
    case "RATE_LIMITED":
      return "rateLimited";
    case "IDEMPOTENCY_IN_PROGRESS":
    case "IDEMPOTENCY_LEASE_LOST":
      return "inProgress";
    case "VALIDATION_FAILED":
    case "CONTEXT_MISMATCH":
    case "IDEMPOTENCY_KEY_REUSED":
      return "validation";
    case "VERSION_PRECONDITION_FAILED":
    case "RULESET_STALE_BASE":
    case "LEASE_LOST":
    case "ENVIRONMENT_MISMATCH":
      return "conflict";
    case "DEPENDENCY_UNAVAILABLE":
    case "ARTIFACT_UNAVAILABLE":
      return "unavailable";
    default:
      return error.status === 504
        ? "gatewayTimeout"
        : error.status >= 500
          ? "server"
          : error.status === 413
            ? "tooLarge"
            : "server";
  }
}

export function adviceForFailure(error: unknown): FailureAdvice {
  const kind = failureKindOf(error);
  const maybeCommitted = mayHaveCommitted(error);
  const api = error instanceof TaxApiError ? error : null;
  return {
    kind,
    whatKey: maybeCommitted ? "failure.committed.what" : (`failure.${kind}.what` as MessageKey),
    todoKey: maybeCommitted ? "failure.committed.todo" : (`failure.${kind}.todo` as MessageKey),
    retryable: RETRYABLE_KINDS.has(kind) && !maybeCommitted,
    maybeCommitted,
    correlationId: api?.correlationId,
    requestId: api?.requestId,
  };
}

/** One sentence pair for inline notices and toasts: what happened, then what to do. */
export function failureSentence(t: (key: MessageKey) => string, error: unknown): string {
  const advice = adviceForFailure(error);
  return `${t(advice.whatKey)} ${t(advice.todoKey)}`;
}
